import React from 'react'
import Table, { TableRow, TableRowCell } from 'core-system/Table'
import Text from 'core-system/Text'
import Chip from 'core-system/Chip'
import moment from 'moment'
import { ScheduledReport } from 'redux/automatedReporting/automatedReportingTypes'
import EditButton from 'features/Programs/Shared/components/EditButton'
import {
  formatCycle,
  formatReportType,
  formatScheduledReportRange,
  formatSegmentNames,
} from '../utils'

interface ScheduledReportsTableProps {
  scheduledReports: ScheduledReport[]
  segmentNamesById: Record<string, string>
  isLoading: boolean
  error: string | null
  onEdit: (scheduledReport: ScheduledReport) => void
}

const HeaderCell = ({ text }: { text: string }) => (
  <TableRowCell noWrap={true}>
    <Text variant='action4'>{text}</Text>
  </TableRowCell>
)

const RowCell = ({ children }: { children: React.ReactNode }) => (
  <TableRowCell style={{ padding: '1.425rem' }} noWrap={true}>
    <Text fontWeight={300} variant='action3'>
      {children}
    </Text>
  </TableRowCell>
)

const ScheduledReportsTable = React.memo(
  ({
    scheduledReports,
    segmentNamesById,
    isLoading,
    error,
    onEdit,
  }: ScheduledReportsTableProps) => {
    const emptyMessage = error
      ? 'Unable to load scheduled reports.'
      : 'No scheduled reports found.'

    return (
      <Table
        headerCells={[
          <HeaderCell text='Name' />,
          <HeaderCell text='Report Type' />,
          <HeaderCell text='Segments' />,
          <HeaderCell text='Cycle' />,
          <HeaderCell text='Next Report Date' />,
          <HeaderCell text='Report Range' />,
          <HeaderCell text='Day of Month' />,
          <HeaderCell text='Status' />,
          <HeaderCell text='Actions' />,
        ]}
        rows={scheduledReports.map((scheduledReport) => (
          <TableRow key={scheduledReport.id}>
            <RowCell>{scheduledReport.name}</RowCell>
            <RowCell>{formatReportType(scheduledReport.reportType)}</RowCell>
            <RowCell>
              {formatSegmentNames(scheduledReport, segmentNamesById)}
            </RowCell>
            <RowCell>{formatCycle(scheduledReport.cycle)}</RowCell>
            <RowCell>
              {moment(scheduledReport.nextReportDate).format('MM/DD/YYYY')}
            </RowCell>
            <RowCell>{formatScheduledReportRange(scheduledReport)}</RowCell>
            <RowCell>{scheduledReport.dayOfMonth}</RowCell>
            <RowCell>
              <Chip variant={scheduledReport.isActive ? 'green' : 'grey'}>
                {scheduledReport.isActive ? 'Active' : 'Inactive'}
              </Chip>
            </RowCell>
            <TableRowCell style={{ padding: '1.425rem' }} noWrap={true}>
              <EditButton onClick={() => onEdit(scheduledReport)} />
            </TableRowCell>
          </TableRow>
        ))}
        maxRows={10}
        emptyMessage={emptyMessage}
        isLoading={isLoading}
        showPagination={scheduledReports.length > 10}
      />
    )
  }
)

if (process.env.NODE_ENV !== 'production') {
  ScheduledReportsTable.displayName = 'ScheduledReportsTable'
}

export default ScheduledReportsTable
